import React, { useEffect, useState, useCallback, useRef } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Add from '@mui/icons-material/Add';
import Edit from '@mui/icons-material/Edit';
import Delete from '@mui/icons-material/Delete';
import { apiUrl, fetchJson } from '../../apiBase';
import Skeleton from '@mui/material/Skeleton';

export default function TeachersManager({ schoolId, onError, onSuccess }) {
  const activeSchool = useRef(schoolId);
  activeSchool.current = schoolId;
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [id, setId] = useState('');
  const [name, setName] = useState('');
  const [surname, setSurname] = useState('');
  const [phone, setPhone] = useState('');
  const [branches, setBranches] = useState([]);
  const [snack, setSnack] = useState('');
  const [lessons, setLessons] = useState([]);
  const [classes, setClasses] = useState([]);
  const [classIds, setClassIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [deleteDlgOpen, setDeleteDlgOpen] = useState(false);
  const [teacherToDelete, setTeacherToDelete] = useState(null);



  const resetForm = () => {
    setId(''); setName(''); setSurname(''); setPhone(''); setBranches([]);
    setClassIds([]);
  };

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [res, classesRes] = await Promise.all([
        fetchJson(apiUrl(`/admin/teachers/${encodeURIComponent(schoolId)}`)),
        fetchJson(apiUrl(`/admin/classes/${encodeURIComponent(schoolId)}/manage`))
      ]);
      if (!res.ok || !classesRes.ok) throw new Error('load');
      const [items, classItems] = await Promise.all([res.json(), classesRes.json()]);
      if (activeSchool.current !== schoolId) return;
      setTeachers(Array.isArray(items) ? items : (items.teachers || []));
      setClasses(Array.isArray(classItems) ? classItems : []);
    } catch (e) {
      if (activeSchool.current === schoolId) onError && onError('Öğretmenler yüklenemedi');
    } finally {
      if (activeSchool.current === schoolId) setLoading(false);
    }
  }, [schoolId, onError]);

  useEffect(() => { if (schoolId) refresh(); }, [refresh, schoolId]);

  // Load lessons (Ders) to use as options for Branş select
  const loadLessons = useCallback(async () => {
    try {
      const res = await fetch(
        apiUrl(`/admin/lessons/${encodeURIComponent(schoolId)}`),
        { credentials: 'include' }
      );
      if (!res.ok) throw new Error('lessons');
      const data = await res.json();
      if (activeSchool.current !== schoolId) return;
      setLessons(Array.isArray(data) ? data : (data.lessons || []));
    } catch (e) {
      // keep silent in UI; teacher form still usable, just no options
    }
  }, [schoolId]);

  // Fetch lessons when dialog opens or school changes
  useEffect(() => { if (dlgOpen && schoolId) loadLessons(); }, [dlgOpen, schoolId, loadLessons]);

  useEffect(() => { setDlgOpen(false); setBranches([]); setClassIds([]); setTeachers([]); setClasses([]); }, [schoolId]);

  const openNew = () => { setEditing(null); resetForm(); setDlgOpen(true); };
  const openEdit = (t) => {
    setEditing(t);
    setId(String(t?.id ?? ''));
    setName(t.name || '');
    setSurname(t.surname || '');
    setPhone(t.phone || '');
    setBranches(t.branches || (t.branch ? [t.branch] : []));
    setClassIds((t.class_ids || []).map(String));
    setDlgOpen(true);
  };

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const idTrim = String(id ?? '').trim();
      const nameTrim = name.trim();
      const surnameTrim = surname.trim();
      const phoneTrim = phone.trim();
      if (!editing && (!nameTrim || !surnameTrim || !phoneTrim)) {
        onError && onError('Ad, soyad ve telefon gerekli');
        return;
      }
      const body = {
        ...((!editing && idTrim) ? { id: idTrim } : {}),
        name: nameTrim || undefined,
        surname: surnameTrim || undefined,
        phone: phoneTrim || undefined,
        branches,
        class_ids: classIds.map(Number)
      };
      let res;
      if (editing && editing.id) {
        res = await fetchJson(
          apiUrl(`/admin/teachers/${encodeURIComponent(schoolId)}/${encodeURIComponent(editing.id)}`),
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
          }
        );
      } else {
        res = await fetchJson(
          apiUrl(`/admin/teachers/${encodeURIComponent(schoolId)}`),
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
          }
        );
      }
      if (res.status === 401) { onError && onError('Yetki gerekli. Lütfen yönetici olarak giriş yapın.'); return; }
      if (!res.ok) {
        let msg = 'Öğretmen kaydedilemedi';
        try {
          const err = await res.json();
          const detail = err && (err.detail || err.message);
          if (detail) {
            msg = Array.isArray(detail)
              ? detail.map((d) => d.msg || d.message || '').filter(Boolean).join(', ')
              : String(detail);
          }
        } catch (_) {}
        throw new Error(msg);
      }
      onSuccess && onSuccess('Öğretmen kaydedildi');
      setDlgOpen(false); setEditing(null);
      refresh();
    } catch (e) { onError && onError((e && e.message) || 'Öğretmen kaydedilemedi'); }
    finally { setSaving(false); }
  };

  const askDelete = (t) => {
    setTeacherToDelete(t || null);
    setDeleteDlgOpen(true);
  };

  const closeDelete = () => {
    setDeleteDlgOpen(false);
    setTeacherToDelete(null);
  };

  const remove = async () => {
    if (!teacherToDelete?.id) return;
    try {
      const res = await fetchJson(
        apiUrl(`/admin/teachers/${encodeURIComponent(schoolId)}/${encodeURIComponent(teacherToDelete.id)}`),
        { method: 'DELETE' }
      );
      if (res.status === 401) { onError && onError('Yetki gerekli. Lütfen yönetici olarak giriş yapın.'); return; }
      if (!res.ok) {
        let msg = 'Öğretmen silinemedi';
        try {
          const err = await res.json();
          const detail = err && (err.detail || err.message);
          if (detail) {
            msg = Array.isArray(detail)
              ? detail.map((d) => d.msg || d.message || '').filter(Boolean).join(', ')
              : String(detail);
          }
        } catch (_) {}
        throw new Error(msg);
      }
      onSuccess && onSuccess('Öğretmen silindi');
      closeDelete();
      refresh();
    } catch (e) { onError && onError((e && e.message) || 'Öğretmen silinemedi'); }
  };

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper sx={{ p: 2, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Öğretmenler</Typography>
              <Button startIcon={<Add />} size="small" variant="contained" onClick={openNew} disabled={loading}>Yeni Öğretmen</Button>
            </Box>
            {loading ? (
              <List dense>
                {Array.from({ length: 6 }).map((_, i) => (
                  <ListItem key={`s-${i}`}>
                    <ListItemText
                      primary={<Skeleton variant="text" width="50%" />}
                      secondary={<Skeleton variant="text" width="30%" />}
                    />
                  </ListItem>
                ))}
              </List>
            ) : teachers.length === 0 ? (
              <Box sx={{ py: 3, textAlign: 'center', color: 'text.secondary' }}>
                <Typography variant="body1" sx={{ mb: 2 }}>Henüz öğretmen eklenmedi</Typography>
                <Button startIcon={<Add />} variant="contained" size="small" onClick={openNew}>+ Yeni Öğretmen</Button>
              </Box>
            ) : (
              <List dense>
                {teachers.map((t) => (
                  <ListItem key={t.id}>
                    <ListItemText
                      primary={<Typography variant="body1" sx={{ fontWeight: 500 }}>{`${t.name || ''} ${t.surname || ''}`.trim() || t.id}</Typography>}
                      secondary={`${t.id}${t.branches?.length ? ` • ${t.branches.join(', ')}` : t.branch ? ` • ${t.branch}` : ''}${t.phone ? ` • ${t.phone}` : ''} | Sınıflar: ${(t.class_ids || []).map((classId) => classes.find((cls) => String(cls.id) === String(classId))?.name || String(classId)).join(', ') || '-'}${t.responsible_class_names ? ` | Rehberlik: ${t.responsible_class_names}` : ''}`}
                    />
                    <ListItemSecondaryAction>
                      <IconButton edge="end" aria-label={`${t.name} ${t.surname} öğretmenini düzenle`} onClick={() => openEdit(t)}><Edit /></IconButton>
                      <IconButton edge="end" aria-label={`${t.name} ${t.surname} öğretmenini sil`} onClick={() => askDelete(t)}><Delete /></IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={dlgOpen} onClose={() => { if (!saving) setDlgOpen(false); }} fullWidth maxWidth="sm">
        <DialogTitle>Öğretmen Formu</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <TextField label="Öğretmen ID (boş bırakılırsa otomatik)" inputProps={{ 'aria-label': 'Öğretmen ID' }} value={id} onChange={(e) => setId(e.target.value)} size="small" disabled={Boolean(editing)} />
            {(() => {
              const names = (() => {
                const arr = (lessons || []).filter((l) => !['MIXED_HOMEWORK', 'TOPLAM'].includes(l?.code)).map((l) => l?.code === 'SINIF' ? 'Sınıf Öğretmenliği' : typeof l === 'string' ? l : (l?.name || '')).filter(Boolean);
                // ensure existing value is present to avoid Autocomplete value warnings
                branches.forEach((branch) => { if (!arr.includes(branch)) arr.push(branch); });
                return arr.sort((a, b) => a.localeCompare(b, 'tr'));
              })();
              return (
                <Autocomplete
                  multiple
                  options={names}
                  value={branches}
                  onChange={(_, values) => setBranches(values)}
                  fullWidth
                  size="small"
                  renderInput={(params) => (
                    <TextField {...params} label="Bu Kurumdaki Branşlar" inputProps={{ ...params.inputProps, 'aria-label': 'Bu Kurumdaki Branşlar' }} />
                  )}
                  noOptionsText="Ders bulunamadı"
                />
              );
            })()}
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 2 }}>
            <TextField label="Ad" inputProps={{ 'aria-label': 'Ad' }} value={name} onChange={(e) => setName(e.target.value)} size="small" />
            <TextField label="Soyad" inputProps={{ 'aria-label': 'Soyad' }} value={surname} onChange={(e) => setSurname(e.target.value)} size="small" />
          </Box>
          <Box sx={{ mt: 2 }}>
            <TextField label="Telefon" inputProps={{ 'aria-label': 'Telefon' }} value={phone} onChange={(e) => setPhone(e.target.value)} fullWidth size="small" />
          </Box>
          <Box sx={{ mt: 2 }}>
            <Autocomplete
              multiple
              options={classes.map((cls) => String(cls.id))}
              value={classIds}
              getOptionLabel={(classId) => {
                const cls = classes.find((item) => String(item.id) === classId);
                return cls ? `${cls.name} (${cls.education_year || ''})` : classId;
              }}
              getOptionDisabled={(classId) => classes.some((cls) => String(cls.id) === classId && String(cls.class_representative_teacher_id) === String(editing?.id))}
              onChange={(_, values) => {
                const responsibleIds = editing ? classes.filter((cls) => String(cls.class_representative_teacher_id) === String(editing.id)).map((cls) => String(cls.id)) : [];
                setClassIds([...new Set([...values, ...responsibleIds])]);
              }}
              renderInput={(params) => <TextField {...params} label="Atanmış Sınıflar" helperText="Rehber öğretmen atamasını kaldırmak için önce şube formundan rehber öğretmeni değiştirin." />}
              noOptionsText="Sınıf bulunamadı"
              size="small"
              disabled={saving}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlgOpen(false)} disabled={saving}>İptal</Button>
          <Button variant="contained" onClick={save} disabled={saving}>{saving ? 'Kaydediliyor…' : 'Kaydet'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDlgOpen} onClose={closeDelete} fullWidth maxWidth="xs">
        <DialogTitle>Öğretmeni Sil</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            {teacherToDelete ? `${teacherToDelete.name || ''} ${teacherToDelete.surname || ''} silinsin mi?` : 'Bu öğretmen silinsin mi?'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDelete}>İptal</Button>
          <Button color="error" variant="contained" onClick={remove}>Sil</Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={Boolean(snack)} autoHideDuration={2000} onClose={() => setSnack('')}>
        <Alert severity="success">{snack}</Alert>
      </Snackbar>
    </>
  );
}

